/**
 * Classificazione degli errori non gestiti (window 'error' / 'unhandledrejection')
 * prima di mostrare l'avviso rosso "Si è verificato un errore" con il pulsante Segnala.
 *
 * L'avviso serve per i difetti del CRM. Non va mostrato per:
 *  - errori già noti e innocui (lista storica, es. ResizeObserver, chunk mancanti);
 *  - problemi di rete o di sessione passeggeri (computer appena risvegliato, Wi-Fi
 *    caduto): non sono difetti e l'utente non può farci nulla;
 *  - errori nati in script esterni o estensioni del browser.
 *
 * Modulo puro: nessun accesso a window, l'origine del sito arriva dal chiamante.
 */

export type AutoReportDecision = 'report' | 'ignored' | 'transient' | 'external';

export interface AutoReportInput {
  message: string;
  /** Stack dell'errore, se disponibile */
  stack?: string | null;
  /** File che ha lanciato l'errore (ErrorEvent.filename), se disponibile */
  filename?: string | null;
  /** Origine del CRM (window.location.origin) */
  origin: string;
}

/** Errori già ignorati da sempre (prima vivevano in main.tsx). */
export const IGNORED_ERRORS = [
  'ResizeObserver loop',
  'ResizeObserver loop completed',
  'Loading chunk',
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
  'Network Error',
  'AbortError',
  'cancelled',
  'Script error.',
  'Objects are not valid as a React child',
];

/** Rete o sessione passeggere: "User not authenticated" è lanciato da lib/utils.ts
 * quando la sessione non si legge subito dopo il risveglio del computer. */
export const TRANSIENT_ERRORS = [
  'Failed to fetch',
  'NetworkError',
  'Load failed',
  'User not authenticated',
];

/** Firme di errori di script di terzi (pubblicità, tracciamento, estensioni). */
const EXTERNAL_MESSAGE_PATTERNS: RegExp[] = [/reading '?M_ID'?/];

const EXTENSION_SCHEMES = /^(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//i;
const URL_IN_STACK = /((?:https?|(?:chrome|moz|safari|safari-web|ms-browser)-extension):\/\/[^\s)]+)/gi;

function isOwnUrl(url: string, origin: string): boolean {
  return !!origin && url.startsWith(origin);
}

/** true se l'errore nasce fuori dal nostro codice. */
export function isExternalOrigin(stack: string | null | undefined, filename: string | null | undefined, origin: string): boolean {
  if (filename) {
    if (EXTENSION_SCHEMES.test(filename)) return true;
    if (/^https?:\/\//i.test(filename) && !isOwnUrl(filename, origin)) return true;
  }
  if (!stack) return false;
  // Il primo indirizzo dello stack è il punto in cui l'errore è nato.
  const firstUrl = stack.match(URL_IN_STACK)?.[0];
  // Stack senza indirizzi (es. "<anonymous>"): non si può dire, resta nostro.
  if (!firstUrl) return false;
  return !isOwnUrl(firstUrl, origin);
}

export function classifyAutoReportError(input: AutoReportInput): AutoReportDecision {
  const message = input.message || '';
  if (IGNORED_ERRORS.some((e) => message.includes(e))) return 'ignored';
  if (TRANSIENT_ERRORS.some((e) => message.includes(e))) return 'transient';
  if (EXTERNAL_MESSAGE_PATTERNS.some((re) => re.test(message))) return 'external';
  if (isExternalOrigin(input.stack, input.filename, input.origin)) return 'external';
  return 'report';
}

/** Stack da allegare alla segnalazione, limitato per non gonfiare il context. */
export function stackForReport(stack: string | null | undefined, maxLength = 4000): string | undefined {
  if (!stack) return undefined;
  return stack.length > maxLength ? `${stack.slice(0, maxLength)}…` : stack;
}
